<script setup>
import { computed, onMounted, watch, onBeforeUnmount, ref } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useI18n } from 'vue-i18n';

import { announcePhoneReleased } from 'dashboard/helper/phoneConnectionChannel';
import { restorePhoneConnection } from 'dashboard/helper/phoneAutoConnect';

const { t } = useI18n();

const store = useStore();
const status = useMapGetter('phone/getStatus');
const configured = useMapGetter('phone/configured');
const phoneQueues = useMapGetter('phoneQueues/getPhoneQueues');
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');

const totalWaitingCallers = computed(() => {
  if (!phoneQueues.value || phoneQueues.value.length === 0) return null;
  const currentUserId = currentUser.value.id;
  if (!currentUserId) return null;
  return phoneQueues.value.reduce((total, queue) => {
    const isMember =
      queue.agents && queue.agents.some(agent => agent.id === currentUserId);
    if (!isMember) return total;
    return total + (queue.callers ? queue.callers.length : 0);
  }, 0);
});

const waitingCallersBubbleColor = computed(() => {
  const count = totalWaitingCallers.value;
  if (count === 0) return 'bg-green-500';
  if (!count) return null;
  if (count <= 5) return 'bg-orange-500';
  return 'bg-red-500';
});

const waitingCallersTooltip = computed(() => {
  if (!waitingCallersBubbleColor.value) return null;
  if (totalWaitingCallers.value === 0) return t('SIDEBAR.NO_WAITING_CALLERS');
  return t('SIDEBAR.WAITING_CALLERS', { count: totalWaitingCallers.value });
});

const wakeLock = ref(null);

// If this tab owns the phone and is being closed, let other tabs know the phone
// is free again so they drop their "connected in another tab" state.
const releaseOwnershipOnUnload = () => {
  if (status.value !== 'offline') {
    announcePhoneReleased(currentAccountId.value);
  }
};

const acquireWakeLock = async () => {
  if (!('wakeLock' in navigator)) return;
  try {
    wakeLock.value = await navigator.wakeLock.request('screen');

    wakeLock.value.addEventListener('release', () => {
      wakeLock.value = null;
    });
  } catch {
    // Wake lock request failed (e.g. tab not visible)
  }
};

const releaseWakeLock = async () => {
  if (wakeLock.value) {
    await wakeLock.value.release();

    wakeLock.value = null;
  }
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible' && status.value === 'connected') {
    acquireWakeLock();
  }
};

watch(status, (newStatus, oldStatus) => {
  if (newStatus === 'connected' && oldStatus !== 'connected') {
    acquireWakeLock();
    document.addEventListener('visibilitychange', handleVisibilityChange);
  } else if (newStatus !== 'connected' && oldStatus === 'connected') {
    releaseWakeLock();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
});

onBeforeUnmount(() => {
  releaseWakeLock();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('beforeunload', releaseOwnershipOnUnload);
});

onMounted(async () => {
  await store.dispatch('inboxes/get');
  await store.dispatch('userPhoneCredentials/get');
  await store.dispatch('phone/checkChromeExtension');

  // Coordinate across tabs: only one tab holds the phone. Other tabs learn who
  // the owner is and offer a "go to that tab" button instead of connecting.
  store.dispatch('phone/initTabCoordination');
  window.addEventListener('beforeunload', releaseOwnershipOnUnload);

  await restorePhoneConnection(store);
});
</script>

<template>
  <div class="my-1 ml-auto">
    <div
      v-if="configured"
      v-tooltip.top="waitingCallersTooltip"
      :class="waitingCallersBubbleColor"
      class="flex py-1 px-2 rounded-full leading-none text-[12px]"
    >
      <div v-if="totalWaitingCallers > 0" class="pr-1 text-white">
        {{ totalWaitingCallers }}
      </div>
      <fluent-icon
        :icon="status === 'connected' ? 'phone' : 'phone-off'"
        :class="{
          'text-white': waitingCallersBubbleColor,
        }"
        size="12"
        icon-lib="tabler"
      />
    </div>
  </div>
</template>
